import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

const EMPTY = {
  title: "", date: "", start_time: "", end_time: "", location: "", music: "",
  price: 45, capacity: 45, description_fr: "", description_en: "",
  cover_image: "", booking_open: true, published: false,
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");

  const load = useCallback(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/admin/upload", fd);
        payload.cover_image = up.url;
      }
      if (payload.id) {
        await api.put(`/admin/events/${payload.id}`, payload);
      } else {
        await api.post("/admin/events", payload);
      }
      toast.success("Événement enregistré");
      setForm(null);
      setFile(null);
      setPreview("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Événement supprimé");
    load();
  };

  const togglePublish = async (ev) => {
    await api.put(`/admin/events/${ev.id}`, { ...ev, published: !ev.published });
    load();
  };

  const field = (key, label, type = "text", extra = {}) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-earth mb-2">{label}</label>
      <input data-testid={`event-form-${key}`} type={type} className="input-elegant" value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: type === "number" ? Number(e.target.value) : e.target.value })} {...extra} />
    </div>
  );

  if (form) {
    return (
      <div data-testid="event-form">
        <h1 className="font-serif text-3xl text-ink">{form.id ? "Modifier l'événement" : "Nouvel événement"}</h1>
        <div className="w-16 h-px bg-gold mt-4" />
        <form onSubmit={save} className="mt-8 bg-ivory border border-champagne rounded-2xl p-7 max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-5">
          {field("title", "Titre")}
          {field("location", "Lieu (description)")}
          {field("date", "Date", "date", { required: true })}
          {field("music", "Musique (ex : Organic & Deep House)")}
          {field("start_time", "Heure de début", "time", { required: true })}
          {field("end_time", "Heure de fin", "time", { required: true })}
          {field("price", "Prix par personne (€)", "number", { min: 0, step: "0.01", required: true })}
          {field("capacity", "Nombre de places", "number", { min: 1, required: true })}
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.15em] text-earth mb-2">Image de couverture</label>
            {(preview || form.cover_image) && (
              <img data-testid="event-cover-preview" src={preview || form.cover_image} alt="Aperçu de la couverture"
                className="w-full h-48 object-cover rounded-xl border border-champagne mb-3" />
            )}
            <label data-testid="event-cover-upload-btn" className="btn-outline !py-3 cursor-pointer inline-flex">
              Choisir une image
              <input data-testid="event-cover-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
                }} />
            </label>
            {form.cover_image && !file && (
              <p className="text-xs text-earth/70 mt-2">Image actuelle conservée si aucune nouvelle image n'est choisie.</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.15em] text-earth mb-2">Description (FR)</label>
            <textarea data-testid="event-form-description-fr" rows={3} className="input-elegant" value={form.description_fr}
              onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.15em] text-earth mb-2">Description (EN)</label>
            <textarea data-testid="event-form-description-en" rows={3} className="input-elegant" value={form.description_en}
              onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
          </div>
          <label className="flex items-center gap-3 text-sm text-earth">
            <input data-testid="event-form-booking-open" type="checkbox" checked={form.booking_open}
              onChange={(e) => setForm({ ...form, booking_open: e.target.checked })} className="accent-terracotta w-4 h-4" />
            Réservations ouvertes
          </label>
          <label className="flex items-center gap-3 text-sm text-earth">
            <input data-testid="event-form-published" type="checkbox" checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-terracotta w-4 h-4" />
            Publié sur le site
          </label>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="event-save-btn" type="submit" className="btn-primary !py-3">Enregistrer</button>
            <button data-testid="event-cancel-btn" type="button" onClick={() => { setForm(null); setFile(null); setPreview(""); }} className="btn-outline !py-3">Annuler</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-events">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink">Picnic Electronic</h1>
          <div className="w-16 h-px bg-gold mt-4" />
        </div>
        <button data-testid="new-event-btn" onClick={() => { setForm({ ...EMPTY }); setFile(null); setPreview(""); }} className="btn-primary !py-3">
          <Plus size={16} /> Nouvel événement
        </button>
      </div>
      <div className="mt-8 space-y-4">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-${ev.id.slice(0, 8)}`}
            className="bg-ivory border border-champagne rounded-2xl p-6 flex flex-wrap items-center gap-6">
            <div className="flex-1 min-w-[220px]">
              <h3 className="font-serif text-xl text-ink">{ev.title}</h3>
              <p className="text-sm text-earth mt-1">{ev.date} · {ev.start_time}–{ev.end_time} · {ev.location}</p>
              <p className="text-xs text-earth mt-1">{ev.price} € · {ev.places_booked ?? 0}/{ev.capacity} réservé(s)</p>
            </div>
            <span className={`text-xs uppercase tracking-wide px-3 py-1 rounded-full ${ev.published ? "bg-olive/15 text-olive-deep" : "bg-earth/10 text-earth"}`}>
              {ev.published ? "Publié" : "Brouillon"}
            </span>
            <div className="flex gap-2">
              <button data-testid={`publish-event-${ev.id.slice(0, 8)}`} onClick={() => togglePublish(ev)}
                className="text-xs uppercase tracking-wide px-4 py-2 rounded-full border border-champagne text-earth hover:border-terracotta hover:text-terracotta transition-colors">
                {ev.published ? "Dépublier" : "Publier"}
              </button>
              <button data-testid={`edit-event-${ev.id.slice(0, 8)}`} onClick={() => { setForm({ ...EMPTY, ...ev }); setFile(null); setPreview(""); }}
                className="w-9 h-9 rounded-full border border-champagne flex items-center justify-center text-earth hover:border-terracotta hover:text-terracotta transition-colors">
                <Pencil size={14} />
              </button>
              <button data-testid={`delete-event-${ev.id.slice(0, 8)}`} onClick={() => remove(ev.id)}
                className="w-9 h-9 rounded-full border border-champagne flex items-center justify-center text-earth hover:border-burgundy hover:text-burgundy transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="text-earth">Aucun événement. Créez le premier !</p>}
      </div>
    </div>
  );
}
